import {
  getNetworkOverview,
  getNetworkTeam,
  getNetworkRewards,
  putTeamMemberNote,
  type NetworkBusiness,
  type NetworkEligibility,
} from '@/api/network';
import {
  getEarningsOverview,
  getEarningsTrend,
  pickEarningsKind,
  type EarningsPeriod,
  type EarningsScope,
} from '@/api/earnings';
import {
  minUnitToPantaSigned,
  minUnitToWattSigned,
} from '@/common/utils/money';
import type { NetworkOverview, TeamMember } from '@/types/api';

export interface ReferralOverviewParams {
  page?: number;
  page_size?: number;
  /** 0=交易 1=理财 */
  type?: 0 | 1;
  address?: string;
  eligibility?: NetworkEligibility;
  parentUserId?: string;
  offset?: number;
  limit?: number;
}

export interface ReferralItem {
  id: string;
  wallet_address: string;
  user_level: number | string;
  [key: string]: any;
}

export interface ReferralOverviewResponse {
  total: number;
  valid_total?: number;
  items: ReferralItem[];
  [key: string]: any;
}

export interface MyStatsResponse {
  /** 个人累计收益（展示用人类可读） */
  personal_value: string;
  team_value: string;
  personal_profit_loss_sum: string;
  team_profit_loss_sum: string;
  personal_fund_value: string;
  team_fund_value: string;
  personal_fund_profit_loss_sum: string;
  team_fund_profit_loss_sum: string;
  team_contributed_income: string;
  /** 周期个人 / 周期团队 / 累计团队（交易） */
  period_personal?: string;
  period_team?: string;
  total_team?: string;
  /** 理财同形 */
  fund_period_personal?: string;
  fund_period_team?: string;
  fund_total_team?: string;
  asset?: string;
  fund_asset?: string;
  decimals?: number;
  fund_decimals?: number;
  unclassified_total?: string;
  fund_unclassified_total?: string;
  load_error?: boolean;
  fund_load_error?: boolean;
  personal_turnover?: string;
  team_turnover?: string;
  fund_personal_turnover?: string;
  fund_team_turnover?: string;
}

const toBusiness = (type?: 0 | 1): NetworkBusiness =>
  type === 1 ? 'wealth' : 'trading';

const formatByAsset = (
  amount: string | undefined,
  asset: string | undefined,
  decimals?: number
): string => {
  const raw = String(amount ?? '0');
  const a = String(asset || 'JOULE').toUpperCase();
  try {
    if (a === 'PANTA') return minUnitToPantaSigned(raw, decimals ?? 18);
    return minUnitToWattSigned(raw, decimals ?? undefined);
  } catch {
    return '0';
  }
};

/** 页面展示用资产名：统一大写 JOULE */
const toDisplayAsset = (asset?: string, fallback = 'JOULE'): string => {
  const u = String(asset || fallback).toUpperCase();
  if (u === 'TAKER' || u === 'WATT' || u === 'JOULE') return 'JOULE';
  return u || fallback;
};

/**
 * 团队明细 — network/overview + network/team（显式 business）
 */
export const getReferralOverview = async (
  params?: ReferralOverviewParams
): Promise<ReferralOverviewResponse> => {
  const business = toBusiness(params?.type);
  const page = params?.page ?? 1;
  const pageSize = Math.min(100, params?.page_size ?? params?.limit ?? 20);
  const offset =
    params?.offset != null
      ? Math.max(0, params.offset)
      : Math.max(0, (page - 1) * pageSize);
  const eligibility = params?.eligibility ?? 'all';
  const parentUserId = params?.parentUserId;

  const [overview, team] = await Promise.all([
    getNetworkOverview({ business, parentUserId }).catch(() => {
      throw new Error('NETWORK_OVERVIEW_FAILED');
    }),
    getNetworkTeam({
      business,
      parentUserId,
      scope: 'direct',
      eligibility,
      offset,
      limit: pageSize,
    }).catch(() => ({
      items: [] as TeamMember[],
      nextOffset: null as number | null,
    })),
  ]);

  const items: ReferralItem[] = (team.items || []).map((m) => mapTeamMember(m, business));

  const effectiveCount =
    business === 'wealth'
      ? overview.activeInvestorCount ?? overview.effectiveCount ?? 0
      : overview.effectiveTradingCount ?? overview.effectiveCount ?? 0;

  const performanceRaw =
    business === 'wealth'
      ? overview.teamActivePanta ??
        overview.performanceAmount ??
        overview.teamTurnover
      : overview.performanceAmount ?? overview.teamTurnover;

  return {
    ...overview,
    total: overview.directCount ?? items.length,
    valid_total: effectiveCount,
    items,
    user_level: overview.businessLevel ?? overview.effectiveLevel ?? 0,
    business_level: overview.businessLevel ?? overview.effectiveLevel ?? 0,
    effective_level: overview.effectiveLevel ?? overview.businessLevel ?? 0,
    direct_count: overview.directCount ?? 0,
    team_count: overview.teamCount ?? 0,
    effective_count: effectiveCount,
    effective_trading_count: overview.effectiveTradingCount ?? 0,
    active_investor_count: overview.activeInvestorCount ?? 0,
    dividend_eligible_count: overview.dividendEligibleCount ?? 0,
    team_profit: formatByAsset(
      overview.rewardAmount ?? overview.paidRewards,
      overview.rewardAsset || (business === 'wealth' ? 'PANTA' : 'JOULE')
    ),
    team_flow: formatByAsset(
      performanceRaw,
      overview.performanceAsset || (business === 'wealth' ? 'PANTA' : 'JOULE')
    ),
    reward_asset: toDisplayAsset(
      overview.rewardAsset,
      business === 'wealth' ? 'PANTA' : 'JOULE'
    ),
    performance_asset: toDisplayAsset(
      overview.performanceAsset,
      business === 'wealth' ? 'PANTA' : 'JOULE'
    ),
    rewards_visible: overview.rewardsVisible !== false && !parentUserId,
    self_turnover: formatByAsset(
      overview.upgrade?.selfCurrent ?? overview.selfTurnover,
      overview.upgrade?.unit || (business === 'wealth' ? 'USDT' : 'JOULE'),
      overview.upgrade?.decimals
    ),
    team_turnover: formatByAsset(
      overview.upgrade?.teamCurrent ?? overview.teamTurnover,
      overview.upgrade?.unit || (business === 'wealth' ? 'USDT' : 'JOULE'),
      overview.upgrade?.decimals
    ),
    upgrade: overview.upgrade,
    next_level: overview.upgrade?.nextLevel?.level ?? overview.nextLevel?.level,
    nextLevel: overview.upgrade?.nextLevel ?? overview.nextLevel,
    parentUserId: overview.parentUserId ?? parentUserId ?? null,
    memberDepth: overview.memberDepth,
    direct_referrals: {
      items,
      nextOffset: team.nextOffset,
    },
  };
};

function mapTeamMember(m: TeamMember, business: NetworkBusiness): ReferralItem {
  const id = String(m.userId);
  const wallet =
    m.walletAddress != null && String(m.walletAddress).trim()
      ? String(m.walletAddress).trim()
      : '';
  const displayName = String(m.displayName || '').trim();
  // 有钱包缩写显示钱包；缺失则显示名称，不伪造地址
  const addressOrName = wallet || displayName || id;
  const lv =
    m.level ??
    (business === 'wealth' ? m.financeLevel : m.tradingLevel) ??
    0;
  const effective = m.effective === true;
  const relation =
    String(m.relation || '').toUpperCase() === 'INDIRECT' || m.isDirect === false
      ? 'indirect'
      : 'direct';
  const isWealth = business === 'wealth';
  const perfAsset = isWealth ? 'PANTA' : 'JOULE';
  const personalRaw = isWealth
    ? m.activePanta ?? '0'
    : m.selfTurnover ?? m.contribution ?? '0';
  const personalPerf = formatByAsset(String(personalRaw), perfAsset);
  const teamPerfRaw = isWealth
    ? m.teamActivePanta ?? '0'
    : m.teamTurnover ?? '0';
  const teamPerf = formatByAsset(String(teamPerfRaw), perfAsset);
  const directCount = Number(m.directCount);
  const teamCount = Number(m.teamCount);
  const settledOrderCount = Number(m.settledOrderCount);
  const activeSubscriptionCount = Number(m.activeSubscriptionCount);
  const metricCount = isWealth ? activeSubscriptionCount : settledOrderCount;

  return {
    id,
    wallet_address: addressOrName,
    wallet_address_raw: wallet || null,
    user_level: lv,
    displayName: displayName || addressOrName,
    joined_at: m.joinedAt || '--',
    invite_type: relation,
    status: effective ? 'valid' : 'invalid',
    effective,
    dividend_eligible: m.dividendEligible === true,
    has_active_stake: m.hasActiveStake === true,
    can_expand: m.canExpand === true,
    relation,
    note: m.note || '',
    contribution: personalPerf,
    personal_equity: `${personalPerf} ${toDisplayAsset(perfAsset)}`,
    personal_perf: `${personalPerf} ${toDisplayAsset(perfAsset)}`,
    team_perf: `${teamPerf} ${toDisplayAsset(perfAsset)}`,
    direct_count: Number.isFinite(directCount) ? directCount : 0,
    team_count: Number.isFinite(teamCount) ? teamCount : 0,
    settled_order_count: Number.isFinite(settledOrderCount) ? settledOrderCount : 0,
    active_subscription_count: Number.isFinite(activeSubscriptionCount)
      ? activeSubscriptionCount
      : 0,
    /** 卡片第三指标：交易=已结算订单数；基金=在投套餐数 */
    metric_count: Number.isFinite(metricCount) ? metricCount : 0,
    community_perf: personalPerf,
    area_perf: personalPerf,
    yesterday_stake: '--',
    community_valid: '--',
    community_max_level: '--',
    has_deposit: m.hasDeposit,
    has_trade: m.hasTrade,
    active_panta: formatByAsset(String(m.activePanta ?? '0'), 'PANTA'),
    team_active_panta: formatByAsset(String(m.teamActivePanta ?? '0'), 'PANTA'),
    self_turnover: formatByAsset(String(m.selfTurnover ?? '0'), 'JOULE'),
    team_turnover: formatByAsset(String(m.teamTurnover ?? '0'), 'JOULE'),
  };
}

/** 成员详情弹层 — 带 parentUserId 的分业务概览 */
export const getMemberStats = async (params: {
  member_id: string;
  type?: 0 | 1;
}): Promise<{
  team_profit: string;
  team_flow: string;
  direct_count: number;
  team_count: number;
  rewards_visible?: boolean;
  reward_asset?: string;
  performance_asset?: string;
}> => {
  const business = toBusiness(params.type);
  const overview = await getNetworkOverview({
    business,
    parentUserId: params.member_id,
  }).catch(() => null);

  if (!overview) {
    return {
      team_profit: '0',
      team_flow: '0',
      direct_count: 0,
      team_count: 0,
      rewards_visible: false,
    };
  }

  const rewardAsset = toDisplayAsset(
    overview.rewardAsset,
    business === 'wealth' ? 'PANTA' : 'JOULE'
  );
  const perfAsset = toDisplayAsset(
    overview.performanceAsset,
    business === 'wealth' ? 'PANTA' : 'JOULE'
  );

  return {
    team_profit:
      overview.rewardsVisible === false
        ? ''
        : formatByAsset(overview.rewardAmount ?? overview.paidRewards, rewardAsset),
    team_flow: formatByAsset(
      business === 'wealth'
        ? overview.teamActivePanta ??
            overview.performanceAmount ??
            overview.teamTurnover
        : overview.performanceAmount ?? overview.teamTurnover,
      perfAsset
    ),
    direct_count: overview.directCount ?? 0,
    team_count: overview.teamCount ?? 0,
    rewards_visible: overview.rewardsVisible !== false,
    reward_asset: rewardAsset,
    performance_asset: perfAsset,
  };
};

const overviewToStats = (
  trading: Awaited<ReturnType<typeof getEarningsOverview>> | null,
  wealth: Awaited<ReturnType<typeof getEarningsOverview>> | null
): MyStatsResponse => {
  const tPersonal = pickEarningsKind(trading || undefined, 'PERSONAL');
  const tTeam = pickEarningsKind(trading || undefined, 'TEAM');
  const tUnc = pickEarningsKind(trading || undefined, 'UNCLASSIFIED');
  const wPersonal = pickEarningsKind(wealth || undefined, 'PERSONAL');
  const wTeam = pickEarningsKind(wealth || undefined, 'TEAM');
  const wUnc = pickEarningsKind(wealth || undefined, 'UNCLASSIFIED');

  const tAsset = toDisplayAsset(trading?.asset, 'JOULE');
  const wAsset = toDisplayAsset(wealth?.asset, 'PANTA');
  const tDec = trading?.decimals;
  const wDec = wealth?.decimals;

  return {
    personal_profit_loss_sum: formatByAsset(tPersonal?.totalAmount, tAsset, tDec),
    personal_turnover: trading?.activity ? formatByAsset(trading.activity.personalAmount, trading.activity.asset, trading.activity.decimals) : undefined,
    team_turnover: trading?.activity ? formatByAsset(trading.activity.teamAmount, trading.activity.asset, trading.activity.decimals) : undefined,
    fund_personal_turnover: wealth?.activity ? formatByAsset(wealth.activity.personalAmount, wealth.activity.asset, wealth.activity.decimals) : undefined,
    fund_team_turnover: wealth?.activity ? formatByAsset(wealth.activity.teamAmount, wealth.activity.asset, wealth.activity.decimals) : undefined,
    period_personal: formatByAsset(tPersonal?.periodAmount, tAsset, tDec),
    period_team: formatByAsset(tTeam?.periodAmount, tAsset, tDec),
    total_team: formatByAsset(tTeam?.totalAmount, tAsset, tDec),
    team_contributed_income: formatByAsset(tTeam?.totalAmount, tAsset, tDec),
    team_profit_loss_sum: formatByAsset(tTeam?.periodAmount, tAsset, tDec),
    personal_value: formatByAsset(tPersonal?.periodAmount, tAsset, tDec),
    team_value: formatByAsset(tTeam?.periodAmount, tAsset, tDec),
    personal_fund_profit_loss_sum: formatByAsset(
      wPersonal?.totalAmount,
      wAsset,
      wDec
    ),
    fund_period_personal: formatByAsset(wPersonal?.periodAmount, wAsset, wDec),
    fund_period_team: formatByAsset(wTeam?.periodAmount, wAsset, wDec),
    fund_total_team: formatByAsset(wTeam?.totalAmount, wAsset, wDec),
    team_fund_profit_loss_sum: formatByAsset(wTeam?.totalAmount, wAsset, wDec),
    personal_fund_value: formatByAsset(wPersonal?.periodAmount, wAsset, wDec),
    team_fund_value: formatByAsset(wTeam?.periodAmount, wAsset, wDec),
    asset: tAsset,
    fund_asset: wAsset,
    decimals: tDec,
    fund_decimals: wDec,
    unclassified_total: formatByAsset(tUnc?.totalAmount, tAsset, tDec),
    fund_unclassified_total: formatByAsset(wUnc?.totalAmount, wAsset, wDec),
    load_error: !trading,
    fund_load_error: !wealth,
  };
};

/**
 * 我的统计 — 并行交易/理财 earnings/overview
 */
export const getMyStats = async (period: EarningsPeriod = '1W'): Promise<MyStatsResponse> => {
  const [trading, wealth] = await Promise.all([
    getEarningsOverview('trading', { period, asset: 'Joule' }).catch(() => null),
    getEarningsOverview('wealth', { period, asset: 'PANTA' }).catch(() => null),
  ]);
  if (!trading && !wealth) {
    throw new Error('EARNINGS_OVERVIEW_FAILED');
  }
  return overviewToStats(trading, wealth);
};

export const getTeamRewards = (params?: { offset?: number; limit?: number }) =>
  getNetworkRewards(params);

export const saveTeamMemberNote = putTeamMemberNote;

// —— 团队页收益折线图 ——
export interface ChartDataPoint {
  date: string;
  profit_loss: string;
  /** 最小单位累计，供交叉比较 */
  raw_cumulative?: string;
}

export interface ChartResponse {
  period: string;
  total: number;
  page: number;
  page_size: number;
  items: ChartDataPoint[];
  asset?: string;
  decimals?: number;
}

export interface ChartParams {
  period?: '1d' | '1w' | '1m' | '3m' | 'ytd' | '1y' | 'all' | EarningsPeriod;
  page?: number;
  page_size?: number;
}

const toEarningsPeriod = (period?: ChartParams['period']): EarningsPeriod => {
  const p = String(period || '1W').toUpperCase();
  const allowed: EarningsPeriod[] = ['1D', '1W', '1M', '3M', 'YTD', '1Y', 'ALL'];
  return (allowed.includes(p as EarningsPeriod) ? p : '1W') as EarningsPeriod;
};

const formatBucketLabel = (
  bucketStart: string,
  period: EarningsPeriod
): string => {
  // bucketStart 含 +08:00
  const d = new Date(bucketStart);
  if (Number.isNaN(d.getTime())) return bucketStart || '--';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (period === '1D') return `${hh}:${mm}`;
  if (period === '1Y' || period === 'ALL' || period === 'YTD') return `${y}-${m}`;
  return `${m}-${day}`;
};

const addMinUnits = (a: string, b: string): string => {
  try {
    return (BigInt(String(a || '0')) + BigInt(String(b || '0'))).toString();
  } catch {
    return '0';
  }
};

/**
 * 用 earnings/trend 绘制累计曲线：同一桶 PERSONAL+TEAM 合计
 */
const buildChartFromTrend = async (
  scope: EarningsScope,
  params?: ChartParams
): Promise<ChartResponse> => {
  const period = toEarningsPeriod(params?.period);
  const asset = scope === 'trading' ? 'Joule' : 'PANTA';
  const trend = await getEarningsTrend(scope, {
    period,
    asset: asset as 'Joule' | 'PANTA',
  });

  const byBucket = new Map<
    string,
    { personal: string; team: string; unclassified: string }
  >();
  for (const pt of trend.items || []) {
    const key = pt.bucketStart;
    const cur = byBucket.get(key) || {
      personal: '0',
      team: '0',
      unclassified: '0',
    };
    const kind = String(pt.kind || '').toUpperCase();
    if (kind === 'PERSONAL') cur.personal = pt.cumulative || '0';
    else if (kind === 'TEAM') cur.team = pt.cumulative || '0';
    else if (kind === 'UNCLASSIFIED') cur.unclassified = pt.cumulative || '0';
    byBucket.set(key, cur);
  }

  const keys = Array.from(byBucket.keys()).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  );

  const format = (raw: string) =>
    formatByAsset(raw, String(trend.asset || asset), trend.decimals);

  let items: ChartDataPoint[] = keys.map((key) => {
    const cur = byBucket.get(key)!;
    const combined = addMinUnits(cur.personal, cur.team);
    return {
      date: formatBucketLabel(key, period),
      profit_loss: format(combined),
      raw_cumulative: combined,
    };
  });

  if (!items.length) {
    items = [
      { date: formatBucketLabel(trend.asOf || new Date().toISOString(), period), profit_loss: '0', raw_cumulative: '0' },
      { date: formatBucketLabel(trend.asOf || new Date().toISOString(), period), profit_loss: '0', raw_cumulative: '0' },
    ];
  }

  return {
    period,
    total: items.length,
    page: 1,
    page_size: items.length,
    items,
    asset: String(trend.asset || asset),
    decimals: trend.decimals,
  };
};

/** 合约持仓盈亏图表 */
export const getProfitChart = (params?: ChartParams) =>
  buildChartFromTrend('trading', params);

/** 理财收益图表 */
export const getFundProfitChart = (params?: ChartParams) =>
  buildChartFromTrend('wealth', params);

export type { NetworkOverview, EarningsPeriod };
